// V2 版式：closing（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/closing.ts 逐坐标移植
// 无 GEO（divider/closing 不在 v2geo dump 内），数值全部内联自后端文件并标注来源行。
// 左 accent 半屏（标题 + meta）+ 右 3 条 takeaway（第 3 条 accent 上色）；不渲染 Chrome（meta 行自含）。
// 注意：本版式自铺 paper 底 + 左半 accent 矩形（closing.ts:20-23），覆盖 wrapper 的 accent 满屏底色。

import { SPACING } from "../../lib/tokens.generated";
import { px, pt, titleSize, footOf } from "./shared";
import { luxeExt, LuxeDecorCircle } from "./luxe";
import {
  termTheme, brutalTheme, reportTheme, inkTheme, memphisTheme,
  InkSeal, MemphisMotif,
} from "./themes";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

export default function V2Closing({ c, skin, editable, handlers }: V2LayoutProps) {
  const W = 13.333;
  const H = 7.5;
  const half = W / 2;
  const m = SPACING.marginX;
  const mag = skin.style === "magazine";
  // closing.ts:16-17 —— swiss 下 skin.accent/accentOn 即 theme.accent/accentOn（skin.ts:45），取解析后值
  const accent = skin.accent;
  const accentOn = skin.accentOn;
  // luxe（closing.ts leftFill/leftText 分支）：左半屏墨黑（inkDeep）+ 米白文字，与封面闭环；
  // 注意 v2geo theme 快照无 inkDeep——从 tokens.generated THEMES 取（见 luxe.tsx 头注）
  const ext = luxeExt(skin);
  // 主题库扩展 6 风格左半屏（closing.ts 分支）：term/brutal/report/ink/memphis 均盖 pageDeep 深底左半屏
  const termT = termTheme(skin);
  const brutalT = brutalTheme(skin);
  const reportT = reportTheme(skin);
  const inkT = inkTheme(skin);
  const mpT = memphisTheme(skin);
  const leftFill = (ext || termT || brutalT || reportT || inkT || mpT) ? (skin.pageDeep ?? accent) : accent;
  const leftText = ext ? ext.paper
    : termT ? termT.ink   // term 左半纯黑底上 accentOn 是近黑不可读——用磷光浅绿正文色
      : reportT ? reportT.ink : inkT ? inkT.paper : mpT ? mpT.paper
        : accentOn;
  // closing.ts:20,46-48 —— paper 底与右半墨色取 theme 层（bg=accent 的 skin.page 是 accent 色，不能用作纸底）
  // brutal/term/report/ink/memphis 右半屏分别为白报纸 / CRT 近黑 / 近黑 / 暖纸 / 奶油
  const theme = skin.theme as any;
  const paper = mag ? theme.paper : ext ? ext.paper
    : brutalT ? brutalT.paper : termT ? termT.paper
      : reportT ? reportT.paper : inkT ? inkT.paper : mpT ? mpT.paper
        : theme.grey?.paper ?? skin.page;
  // 右半文字色取 theme 层（closing.ts:104-106）；6 新风格 theme.ink 即右半正文色
  const tInk = brutalT?.ink ?? termT?.ink ?? reportT?.ink ?? inkT?.ink ?? mpT?.ink;
  const ink = mag ? theme.ink : ext ? ext.ink : theme.grey?.ink ?? tInk ?? skin.text;
  const sub = mag ? theme.ink : ext ? ext.ink : theme.grey?.grey3 ?? tInk ?? skin.sub;
  const hl = mag ? theme.paperTint : ext ? skin.hairline : theme.grey?.grey2 ?? skin.hairline;

  // 供料（closing.ts:37-43）：bullets 优先（≤3 条）→ cards 标题 → subtitle；同时记录编辑路径
  const bi = c.blocks.findIndex((b) => b.type === "bullets");
  const ci = c.blocks.findIndex((b) => (b as { type: string }).type === "cards");
  const items: { text: string; path: string }[] = (() => {
    if (bi >= 0) {
      const b = c.blocks[bi];
      if (b.type === "bullets") return b.items.slice(0, 3).map((t, i) => ({ text: t, path: `blocks.${bi}.items.${i}` }));
    }
    if (ci >= 0) {
      const cd = c.blocks[ci] as unknown as { type: "cards"; items: { title: string }[] };
      return cd.items.slice(0, 3).map((it, i) => ({ text: it.title, path: `blocks.${ci}.items.${i}.title` }));
    }
    return [c.subtitle ?? ""].filter(Boolean).map((t) => ({ text: t, path: "subtitle" }));
  })();

  const rx = half + 0.75;              // closing.ts:44
  const rw = W - half - 0.75 - m;      // closing.ts:45

  return (
    <>
      {/* 纸底 + 左半 accent 满高矩形（closing.ts:20-23；luxe/term/brutal/report/ink/memphis 盖深底左半屏） */}
      <div style={{ position: "absolute", inset: 0, background: paper }} />
      <div style={{
        position: "absolute", left: 0, top: 0, width: px(half), height: px(H),
        background: leftFill,
      }} />
      {/* term：左右同深底无边界——中缝处加 1px muted 分隔线；report/memphis 同款（closing.ts 分支） */}
      {(termT || reportT || mpT) && (
        <div style={{
          position: "absolute", left: px(half), top: 0, width: 1, height: px(H),
          background: termT ? termT.muted : reportT ? reportT.muted : mpT!.muted,
        }} />
      )}
      {/* 裸金装饰圆（仅 luxe；落在左半墨黑屏上，与封面闭环） */}
      <LuxeDecorCircle skin={skin} variant="closing" />
      {/* memphis（043 §4.7）：边距母题狂欢（cover/divider/closing 档） */}
      <MemphisMotif skin={skin} variant="closing" />
      {/* brutal（043 §4.3）/report（043 §4.5）：一红守则——左半屏 foot 上方红短线（全页唯一红） */}
      {(brutalT || reportT) && (
        <div style={{
          position: "absolute", left: px(m), top: px(6.32), width: px(1.2),
          height: brutalT ? px(0.06) : px(0.05),
          background: reportT ? reportT.accentRed : brutalT!.accent,
        }} />
      )}
      {/* ink（043 §4.6）：朱印落款——左半屏墨黑右下，白字「印」方章（与封面闭环） */}
      <InkSeal skin={skin} x={half - m - 0.62} y={5.35} />

      {/* 左半：标题（closing.ts:26-30） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(m), top: px(2.5), width: px(half - m - 0.5), height: px(1.8),
          fontSize: pt(titleSize(c.title, Math.round(skin.scale.pageTitle * 1.2))), color: leftText,
          fontFamily: skin.fontHead, fontWeight: mag ? 700 : 400, lineHeight: 1.15,
        }}
      />
      {/* 左半：底部 meta（closing.ts:31-34） */}
      <div style={{
        position: "absolute", left: px(m), top: px(6.6), width: px(half - m - 0.4), height: px(0.3),
        fontSize: pt(skin.scale.meta), color: leftText, fontFamily: skin.fontMono, letterSpacing: "0.15em",
      }}>{footOf()}</div>

      {/* 右半：takeaway 列表（closing.ts:49-66）；序号与 hairline 为静态装饰 */}
      {items.map((it, i) => {
        const y = 2.1 + i * 1.45;
        const isHero = i === items.length - 1 && items.length === 3;
        // brutal（043 §4.3）：一红守则——左半屏红是全页唯一红，hero 条改用白字加粗（不重复用红）
        const heroColor = isHero ? (brutalT ? leftText : accent) : sub;
        return (
          <div key={i}>
            <div style={{
              position: "absolute", left: px(rx), top: px(y), width: px(0.8), height: px(0.5),
              fontSize: pt(20), color: heroColor, fontFamily: skin.fontMono, fontWeight: 700,
            }}>{`0${i + 1}`}</div>
            <EditableText
              value={it.text} path={it.path} editable={editable} multiline
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(rx), top: px(y + 0.5), width: px(rw), height: px(0.75),
                fontSize: pt(isHero ? skin.scale.sectionHead + 2 : skin.scale.sectionHead),
                color: isHero && !brutalT ? accent : ink, fontFamily: skin.fontBody,
                fontWeight: isHero ? 700 : 400,
              }}
            />
            {i < items.length - 1 && (
              <div style={{
                position: "absolute", left: px(rx), top: px(y + 1.2), width: px(rw), height: 1,
                background: hl,
              }} />
            )}
          </div>
        );
      })}
    </>
  );
}
